/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      const src = img.src ? img.src.split('&width=')[0] : img.dataset.src.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml('.product-info__weight');
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__media${theme.settings.blendProductImages ? ' image-blend' : ''}"></div>
          <div class="quick-add-info__details">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            ${weightElem}
            <hr>
            <div class="product-price">
              ${this.getElementHtml('.product-price')}
            </div>
            <div class="text-theme-light text-sm mt-4">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
        <div class="product-options">
          ${this.getElementHtml('.product-options', '.custom-option:not(.quick-add-hidden)')}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
      `;

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}

// Personalization markup is fetched into Quick Add without the scripts from
// main-product.liquid. Register the component globally for drawer usage.
if (!customElements.get('personalization-name-field')) {
  class PersonalizationNameField extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.input = this.querySelector('.personalization-widget__input');
      this.inputShell = this.querySelector('.personalization-widget__input-shell');
      this.editor = this.querySelector('[data-personalization-editor]');
      this.count = this.querySelector('[data-personalization-count]');
      this.counter = this.querySelector('.personalization-widget__counter');
      this.error = this.querySelector('[data-personalization-error]');
      this.suggestions = this.querySelector('[data-personalization-suggestions]');
      this.preview = this.querySelector('[data-personalization-preview]');
      this.previewText = this.querySelector('[data-personalization-preview-text]');
      this.limit = Number(this.dataset.limit) || 15;

      if (!this.input || !this.inputShell || !this.editor || !this.count
          || !this.counter || !this.error || !this.suggestions
          || !this.preview || !this.previewText) return;

      this.initialized = true;

      this.editor.addEventListener('input', () => this.updateText());
      this.editor.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
      this.editor.addEventListener('paste', (event) => {
        event.preventDefault();
        const text = event.clipboardData
          .getData('text/plain')
          .replace(/[\r\n]+/g, ' ');
        document.execCommand('insertText', false, text);
      });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-personalization-value]');
        if (!button) return;
        this.editor.textContent = button.dataset.personalizationValue;
        this.updateText();
        this.editor.focus();
      });

      const formId = this.input.getAttribute('form');
      const form = formId ? document.getElementById(formId) : null;
      if (form) {
        form.addEventListener('submit', (event) => {
          const value = this.cleanValue(this.editor.textContent || '');
          const required = this.editor.getAttribute('aria-required') === 'true';
          if (this.characterCount(value) > this.limit || (required && !value)) {
            event.preventDefault();
            this.editor.focus();
          }
        });
      }

      const productSection = this.closest('.js-product');
      if (productSection) {
        productSection.addEventListener('on:variant:change', () => this.updateLeatherColor());
        productSection.addEventListener('change', (event) => {
          if (event.target.matches('variant-picker input, variant-picker select')) {
            requestAnimationFrame(() => this.updateLeatherColor());
          }
        });
      }

      this.updateText();
      requestAnimationFrame(() => this.updateLeatherColor());
    }

    characterCount(value) {
      return Array.from(value).length;
    }

    cleanValue(value) {
      return value.replace(/\s+/g, ' ').trimStart();
    }

    updateText() {
      const value = this.cleanValue(this.editor.textContent || '');
      const length = this.characterCount(value);
      const isOverLimit = length > this.limit;

      this.input.value = value;
      this.count.textContent = length;
      this.counter.classList.toggle('is-over-limit', isOverLimit);
      this.editor.setAttribute('aria-invalid', isOverLimit ? 'true' : 'false');
      this.inputShell.classList.toggle('is-invalid', isOverLimit);
      this.error.hidden = !isOverLimit;
      this.previewText.textContent = value || 'İsminiz';

      if (isOverLimit) this.renderSuggestions(value);
      else this.suggestions.replaceChildren();
    }

    renderSuggestions(value) {
      const values = this.buildSuggestions(value);
      this.suggestions.replaceChildren();

      values.forEach((suggestion) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'personalization-widget__suggestion';
        button.dataset.personalizationValue = suggestion;
        button.textContent = suggestion;
        button.setAttribute('aria-label', `${suggestion} biçimini kullan`);
        this.suggestions.appendChild(button);
      });

      if (!values.length) {
        const message = document.createElement('span');
        message.textContent = 'Lütfen ismi elle kısaltın.';
        this.suggestions.appendChild(message);
      }
    }

    buildSuggestions(value) {
      const words = value.trim().split(/\s+/).filter(Boolean);
      const results = [];
      const add = (candidate) => {
        const cleaned = candidate.replace(/\s+/g, ' ').trim();
        if (cleaned && this.characterCount(cleaned) <= this.limit && !results.includes(cleaned)) {
          results.push(cleaned);
        }
      };

      if (words.length > 1) {
        const rightShortened = [...words];
        for (let index = rightShortened.length - 1; index > 0; index -= 1) {
          rightShortened[index] = `${Array.from(rightShortened[index])[0]}.`;
          add(rightShortened.join(' '));
          if (results.length) break;
        }

        add(words.map((word, index) => (
          index === 0 || index === words.length - 1
            ? word
            : `${Array.from(word)[0]}.`
        )).join(' '));
        add(`${words[0]} ${words[words.length - 1]}`);
      }

      return results.slice(0, 3);
    }

    normalize(value) {
      return value.toLocaleLowerCase('tr-TR')
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]+/g, '-');
    }

    selectedColorInput() {
      const picker = this.closest('.js-product')?.querySelector('variant-picker');
      if (!picker) return null;

      const optionNames = (this.dataset.colorOptions || 'Renk,Color')
        .split(',')
        .map((name) => this.normalize(name.trim()))
        .filter(Boolean);

      return Array.from(picker.querySelectorAll('input.js-option:checked, select'))
        .find((input) => optionNames.some(
          (name) => this.normalize(input.name || input.id).includes(name)
        ));
    }

    mappedColor(value) {
      const normalized = this.normalize(value || '');
      const colors = {
        siyah: '#74716d', black: '#74716d',
        kahve: '#68422d', kahverengi: '#68422d', brown: '#68422d',
        taba: '#a96332', tan: '#a96332', camel: '#b67843',
        vizon: '#756b62', mink: '#756b62',
        gri: '#696a68', gray: '#696a68', grey: '#696a68',
        antrasit: '#3f4242', anthracite: '#3f4242',
        lacivert: '#202d42', navy: '#202d42',
        mavi: '#365d77', blue: '#365d77',
        yesil: '#315442', green: '#315442',
        bordo: '#5b292d', burgundy: '#5b292d',
        kirmizi: '#7d2f2b', red: '#7d2f2b',
        bej: '#c8ad87', beige: '#c8ad87',
        krem: '#d7c8aa', cream: '#d7c8aa',
        turuncu: '#b7602d', orange: '#b7602d'
      };
      const key = Object.keys(colors).find((name) => normalized.includes(name));
      return key ? colors[key] : null;
    }

    updateLeatherColor() {
      const input = this.selectedColorInput();
      let color = null;
      let colorName = '';

      if (input) {
        colorName = input.value || input.options?.[input.selectedIndex]?.text || '';
        const label = input.id
          ? this.closest('.js-product')?.querySelector(`label[for="${CSS.escape(input.id)}"]`)
          : null;
        if (label) {
          const swatchColor = getComputedStyle(label)
            .getPropertyValue('--swatch-color')
            .trim();
          if (swatchColor && swatchColor !== 'transparent') color = swatchColor;
        }
        color = color || this.mappedColor(colorName);
      }

      const normalized = this.normalize(colorName);
      if (normalized.includes('siyah') || normalized.includes('black')) {
        color = '#74716d';
      }

      if (color) this.preview.style.setProperty('--leather-color', color);
      else this.preview.style.removeProperty('--leather-color');
    }
  }

  customElements.define('personalization-name-field', PersonalizationNameField);
}
